import { BaseReq, Resp } from '@paradigm/api';
import { AsyncResp } from '@paradigm/api/src/fetch-api';
import { retryExecutor } from '@paradigm/utils/src/retry';
import { GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';
import {
  CreateQuoteReq,
  GrfqNewQuote,
  GrfqQuoteV2CreateResp,
} from '#/unified-rfqs/repositories/grfq-types';
import { Report } from '#/unified-rfqs/ui/one-click-trade/types';
import { GrfqService } from '#/unified-rfqs/services/grfq';

interface CreateQuoteArgs extends BaseReq {
  readonly rfqId: number;
  readonly quote: GrfqNewQuote;
}

export interface CreateQuoteReport extends Report {
  readonly request: CreateQuoteReq;
  readonly response: Resp<GrfqQuote | GrfqQuoteV2CreateResp>;
}

export const submitQuote =
  (createQuote: typeof GrfqService.prototype.createQuote) =>
  async (args: CreateQuoteArgs): AsyncResp<CreateQuoteReport> => {
    const { rfqId, signal, quote } = args;

    const createQuoteReq = async () => {
      const request = { rfqId, quote, signal };
      const response = await submitQuoteWithRetries(request, createQuote);
      const report: CreateQuoteReport = {
        message: response.message,
        ok: response.ok,
        side: request.quote.side,
        request,
        response,
      };
      return report;
    };

    return {
      ok: true,
      status: 0,
      error: '',
      message: '',
      data: await createQuoteReq(),
    };
  };

/**
 * We're retrying, but it should fail fast to give fast feedback to the
 * user. This will result in 3 attempts (first + 2 retries) with waits
 * of 50ms and 75ms before the first and second retries respectively.
 */
const QUOTE_SUBMIT_RETRIES = { retries: 2, factor: 1.5, minTimeout: 50 };

const INVALID_PRICE = 3304;

async function submitQuoteWithRetries(
  request: CreateQuoteReq,
  createQuote: typeof GrfqService.prototype.createQuote,
): Promise<Resp<GrfqQuote | GrfqQuoteV2CreateResp>> {
  const { value } = await retryExecutor(async () => {
    const resp = await createQuote(request);

    if (!resp.ok) {
      const isAborted = request.signal?.aborted === true;
      const isRetriable =
        !isAborted &&
        (resp.status === 0 /* Unknown network error */ ||
          resp.status >= 502 /* Infra error */ ||
          /* Retry on invalid price to prevent a transitive failure when
           * the user is trying to update their quotes to new values that
           * cross one of its current quotes */
          resp.data.code === INVALID_PRICE);

      return { ok: false, retriable: isRetriable, value: resp };
    }

    return { ok: true, value: resp };
  }, QUOTE_SUBMIT_RETRIES);

  return value;
}
